var d3 = {
  ...require("d3-axis/dist/d3-axis.min"),
  ...require("d3-scale/dist/d3-scale.min"),
  ...require("d3-selection/dist/d3-selection.min"),
  ...require("d3-shape/dist/d3-shape.min"),
  ...require("d3-interpolate/dist/d3-interpolate.min")
};

var { COLORS, classify, makeTranslate } = require("./lib/helpers");
var { yearFull, yearAbbrev } = require("./lib/helpers/formatDate");
var { isMobile } = require("./lib/breakpoints");

// Render a line chart.
module.exports = function(config) {
  // Setup
  var { dateColumn, valueColumn } = config;

  var aspectWidth = isMobile.matches ? 4 : 16;
  var aspectHeight = isMobile.matches ? 3 : 9;

  var margins = {
    top: 5,
    right: 75,
    bottom: 50,
    left: 50
  };

  var ticksX = 10;
  var ticksY = 10;
  var roundTicksFactor = 5;

  // Mobile
  if (isMobile.matches) {
    ticksX = 5;
    ticksY = 5;
    margins.right = 25;
  }

  // Calculate actual chart dimensions
  var chartWidth = config.width - margins.left - margins.right;
  var chartHeight =
    Math.ceil((config.width * aspectHeight) / aspectWidth) -
    margins.top -
    margins.bottom;

  // Clear existing graphic (for redraw)
  var containerElement = d3.select(config.container);
  containerElement.html("");

  var labels = config.data[0].values.map(d => d[dateColumn]);
  // Create D3 scale objects.
  var xScale = d3
    .scaleBand()
    .domain(labels)
    .range([0, chartWidth])
    .padding(0.1);

  var values = config.data.reduce(
    (acc, d) => acc.concat(d.values.map(v => v[valueColumn])),
    []
  );

  var floors = values.map(
    v => Math.floor(v / roundTicksFactor) * roundTicksFactor
  );
  var min = Math.min.apply(null, floors);

  if (min > 0) {
    min = 0;
  }

  var ceilings = values.map(
    v => Math.ceil(v / roundTicksFactor) * roundTicksFactor
  );
  var max = Math.max.apply(null, ceilings);

  var yScale = d3
    .scaleLinear()
    .domain([min, 300])
    .range([chartHeight, 0]);

  var colorScale = d3
    .scaleOrdinal()
    .domain(
      config.data.map(function(d) {
        return d.name;
      })
    )
    .range([
      "#6f60ff",
      "#3aa01f",
      COLORS.blue3,
      COLORS.orange3,
      COLORS.teal3
    ]);

  // Render the HTML legend.

  var oneLine = config.data.length > 1 ? "" : " one-line";

  var legend = containerElement
    .append("ul")
    .attr("class", "key" + oneLine)
    .selectAll("g")
    .data(config.data)
    .enter()
    .append("li")
    .attr("class", d => "key-item " + classify(d.name));

  legend.append("b").style("background-color", d => colorScale(d.name));

  legend.append("label").text(d => d.name);

  // Create the root SVG element.

  var chartWrapper = containerElement
    .append("div")
    .attr("class", "graphic-wrapper");

  var chartElement = chartWrapper
    .append("svg")
    .attr("width", chartWidth + margins.left + margins.right)
    .attr("height", chartHeight + margins.top + margins.bottom)
    .append("g")
    .attr("transform", `translate(${margins.left},${margins.top})`);

  // Create D3 axes.
  const minLabelWidth = 70; // pixels needed per label
  const maxTicks = Math.max(1, Math.floor(chartWidth / minLabelWidth));
  const step = Math.ceil(labels.length / maxTicks);


  const totalLabels = labels.length;

  var xAxis = d3
    .axisBottom()
    .scale(xScale)
    .tickFormat(d => d)
    .tickSizeOuter(0) // <--- Add this line
    .tickValues(labels.filter((d, i) => {
      // Subtract the current index from the last index.
      // This ensures the very last item (index: totalLabels - 1) always returns 0.
      // 0 % step is always 0, so the right-most label is always included.
      return (totalLabels - 1 - i) % step === 0;
    }));

  var yAxis = d3
    .axisLeft()
    .scale(yScale)
    .ticks(ticksY);

  // Render axes to chart.

  chartElement
    .append("g")
    .attr("class", "x axis")
    .attr("transform", makeTranslate(0, chartHeight))
    .call(xAxis);

  chartElement
    .append("g")
    .attr("class", "y axis")
    .call(yAxis);

  // Render grid to chart.

  var xAxisGrid = function() {
    return xAxis;
  };

  var yAxisGrid = function() {
    return yAxis;
  };

  chartElement
    .append("g")
    .attr("class", "x grid")
    .attr("transform", makeTranslate(0, chartHeight))
    .call(
      xAxisGrid()
        .tickSize(-chartHeight, 0, 0)
        .tickFormat("")
    );

  chartElement
    .append("g")
    .attr("class", "y grid")
    .call(
      yAxisGrid()
        .tickSize(-chartWidth, 0, 0)
        .tickFormat("")
    );
  chartElement
    .append("text")
    .attr("class", "x-label")
    .attr("x", (chartWidth / 3) + 30)
    .attr("y", chartHeight + 35)
    .text("Month");

  chartElement
    .append("text")
    .attr("class", "x-label")
    .attr("x", (chartWidth / 200) - 60)
    .attr("y", (chartHeight / 130) - 42)
    .attr("transform", "rotate(-90)") // or "rotate(45)" for other direction
    .attr("text-anchor", "end")
    .attr("dx", "-0.5em")
    .attr("dy", "0.25em")
    .text("Habeas Filings");

  // Render 0 value line.

  if (min < 0) {
    chartElement
      .append("line")
      .attr("class", "zero-line")
      .attr("x1", 0)
      .attr("x2", chartWidth)
      .attr("y1", yScale(0))
      .attr("y2", yScale(0));
  }

  // Render lines to chart.
  var line = d3
    .line()
    .x(d => xScale(d[dateColumn]) + xScale.bandwidth() / 2)
    .y(d => yScale(d[valueColumn]));

  var tooltip = containerElement
    .append("div")
    .attr("class", "tooltip")
    .style("position", "absolute")
    .style("z-index", "10")
    .style("visibility", "hidden")
    .style("pointer-events", "none") // Prevents the tooltip from flickering
    .style("background", "#fff")
    .style("border", "1px solid #ccc")
    .style("padding", "5px 10px")
    .style("font-size", "12px");

    var dots = chartElement
      .append("g")
      .attr("class", "dots")
      .selectAll("g")
      .data(config.data)
      .enter()
        .append("g")
        .attr('class',d => classify(d.name));

    dots.selectAll("circle")
      .data(function(d, i) {
        d.values.forEach(function(v,k) {
          v.series = d.name;
        });
        return d.values;
      })
      .enter()
        .append("circle")
        .attr("cx", d => xScale(d[dateColumn]) + xScale.bandwidth() / 2)
        .attr("cy", d => yScale(d[valueColumn]))
        .attr("fill", d => colorScale(d.series))
        .attr("stroke-width",function(d){
          var width = .1;
           if (!isMobile.matches) {
             width = .1;
           }

          return width;
        })
        .attr("stroke","#fff")
        .attr("r", 4)
        .attr('class', function(d) {
          if (d.amt == "") {
            return "none";
          }
        });

  chartElement
    .append("g")
    .attr("class", "lines")
    .selectAll("path")
    .data(config.data)
    .enter()
    .append("path")
    .attr("class", d => "line " + classify(d.name))
    .attr("stroke", d => colorScale(d.name))
    .attr("d", d => line(d.values));

  var lastItem = d => d.values[d.values.length - 1];

var labels = chartElement
  .append("g")
  .attr("class", "value")
  .selectAll("text")
  .data(config.data)
  .enter()
  .append("text")
  .attr("x", d => xScale(lastItem(d)[dateColumn]) + xScale.bandwidth() / 2 + 5)
  .attr("y", d => yScale(lastItem(d)[valueColumn]) - 3)
  .style("fill", d => colorScale(d.name));

// Line 1: Series Name (Only show on Desktop)
labels.append("tspan")
  .attr("x", d => xScale(lastItem(d)[dateColumn]) + xScale.bandwidth() / 2 + 5)
  .style("font-weight", "normal") // Series name regular
  .text(d => isMobile.matches ? "" : d.name);

// Line 2: "District: " (Regular weight)
var line2 = labels.append("tspan")
  .attr("x", d => xScale(lastItem(d)[dateColumn]) + xScale.bandwidth() / 2 + 5)
  .attr("dy", isMobile.matches ? "0.3em" : "1.2em")
  .style("font-weight", "normal") 
  .text(d => isMobile.matches ? "" : "District: ");

// Line 2 (continued): The Number (Bold weight)
line2.append("tspan")
  .style("font-weight", "bold") // Just the number is bold
  .text(function(d) {
    var value = Math.round(lastItem(d)[valueColumn]);
    return value;
  });
};
